/**
 * Timer alarm for cooking mode: a short beep through the Web Audio API plus a
 * vibration where the device has one. Both are best-effort — a phone on silent,
 * a browser without vibration, or an audio context the user has not unlocked
 * yet must never break the cook.
 */

let audioContext: AudioContext | null = null;

type AudioContextCtor = typeof AudioContext;

function contextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Create/resume the audio context from inside a user gesture (starting a
 * timer), so the beep is allowed to play later when the timer finishes.
 */
export function primeAlarm(): void {
  const Ctor = contextCtor();
  if (!Ctor) return;
  try {
    audioContext ??= new Ctor();
    if (audioContext.state === 'suspended') void audioContext.resume();
  } catch {
    audioContext = null;
  }
}

/** Three rising beeps — audible over an extractor fan, over in under a second. */
export function playAlarm(): void {
  primeAlarm();
  const ctx = audioContext;
  if (!ctx) return;
  try {
    const start = ctx.currentTime;
    for (let i = 0; i < 3; i += 1) {
      const at = start + i * 0.22;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880 + i * 110, at);
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.2);
    }
  } catch {
    // Autoplay policy or an unusable context — the visual flash still fires.
  }
}

export function vibrateAlarm(): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate([200, 100, 200, 100, 400]);
  } catch {
    // Some browsers throw when the page is not visible.
  }
}

/** Everything a finished timer should do: beep (unless muted) and buzz. */
export function fireAlarm({ muted }: { muted: boolean }): void {
  if (!muted) playAlarm();
  vibrateAlarm();
}
